import { h, render, watch } from 'vue'
import { message } from 'ant-design-vue'
import UploadMask from './components/uploadMask.vue'

/* ==================== 粘贴上传 ====================
 * 拖拽事件天然会落在指令元素上，粘贴事件不会：paste 只会派发到当前焦点元素，
 * 焦点在页面空白处时事件落在 document.body，指令元素上绑 paste 收不到。
 * 所以这里统一在 document 上监听一次，再判断这次粘贴该由哪个指令元素处理。
 */

// 所有启用了粘贴上传的指令元素
const pasteEls = new Set()
// document 上的 paste 监听全局只绑一次
let pasteListenerBound = false

// 调试开关：控制台执行 window.__dropUploadDebug = true 后，粘贴会打印剪贴板明细
const debugLog = (...args) => {
  if (typeof window !== 'undefined' && window.__dropUploadDebug) {
    console.log('[dropUpload]', ...args)
  }
}

// 元素是否可见（display:none / 未挂载的上传区域不参与粘贴）
const isVisible = (el) => !!(el.offsetParent || el.getClientRects().length)

// 元素在 DOM 中的深度：多个指令元素嵌套时，取最内层的那个处理
const getDepth = (el) => {
  let depth = 0
  let node = el
  while (node && node.parentElement) {
    depth++
    node = node.parentElement
  }
  return depth
}

/**
 * data URL 转 File：部分来源（如微信）粘贴时不给 Files，只在 text/html 里带一张 base64 图
 * @param {string} dataUrl
 * @returns {File|null}
 */
const dataUrlToFile = (dataUrl) => {
  try {
    const [meta, base64] = dataUrl.split(',')
    const mime = meta.match(/data:([^;]+)/)?.[1] || 'image/png'
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    const ext = mime.split('/')[1]?.split('+')[0] || 'png'
    return new File([bytes], `pasted-${Date.now()}.${ext}`, { type: mime })
  } catch (err) {
    return null
  }
}

/**
 * 截图粘贴拿到的 File 可能没有文件名或没有扩展名，
 * 而后续的类型校验、bizType 判断都依赖扩展名，这里按 MIME 补一个合法文件名
 */
const normalizeFile = (file) => {
  if (file.name && file.name.includes('.')) return file
  const ext = String(file.type || '')
    .split('/')[1]
    ?.split('+')[0]
  const name = `pasted-${Date.now()}-${Math.floor(Math.random() * 1000)}.${ext || 'png'}`
  try {
    return new File([file], name, {
      type: file.type,
      lastModified: file.lastModified || Date.now()
    })
  } catch (err) {
    // 个别浏览器不支持 File 构造函数时退回原文件
    return file
  }
}

/**
 * 从剪贴板事件中取出文件
 * @param {ClipboardEvent} e
 * @returns {Array<File>}
 */
const getFilesFromClipboard = (e) => {
  const clipboardData = e.clipboardData || window.clipboardData
  if (!clipboardData) return []

  const files = []
  const items = clipboardData.items
  if (items && items.length) {
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile()
        if (file) files.push(file)
      }
    }
  }
  // items 不可用时退回 files
  if (!files.length && clipboardData.files && clipboardData.files.length) {
    files.push(...Array.from(clipboardData.files))
  }

  // 仍然没有文件时，尝试从 text/html 里取内嵌的 base64 图（微信等来源可能走这条路）
  if (!files.length) {
    const html = clipboardData.getData ? clipboardData.getData('text/html') : ''
    const src = html && html.match(/<img[^>]+src=["']([^"']+)["']/i)?.[1]
    if (src && src.startsWith('data:')) {
      const file = dataUrlToFile(src)
      if (file) files.push(file)
    }
  }

  return files
    .filter((file) => file && file.size > 0 && !String(file.name || '').startsWith('.'))
    .map(normalizeFile)
}

/**
 * 剪贴板里是否存在图片意图（有 Files，或 items 里有 image/*，或 html 里有 img）
 * 用于「看起来粘了图却取不到文件」时给出明确提示，而不是静默无反应
 */
const hasImageIntent = (clipboardData) => {
  if (!clipboardData) return false
  const types = Array.from(clipboardData.types || [])
  if (types.includes('Files')) return true
  const items = Array.from(clipboardData.items || [])
  if (items.some((item) => String(item.type || '').startsWith('image/'))) return true
  if (types.includes('text/html')) {
    const html = clipboardData.getData ? clipboardData.getData('text/html') : ''
    return /<img[\s>]/i.test(html || '')
  }
  return false
}

/** 元素是否可编辑（输入框、文本域、富文本）：焦点在这类元素上时，粘贴应归它自己 */
const isEditable = (node) => {
  if (!node || node.nodeType !== 1) return false
  if (node.isContentEditable) return true
  const tag = node.tagName
  if (tag === 'TEXTAREA') return true
  if (tag !== 'INPUT') return false
  const type = String(node.type || 'text').toLowerCase()
  return ['text', 'search', 'url', 'email', 'tel', 'password', 'number'].includes(type)
}

/**
 * 判断某个指令元素是否应该响应本次粘贴
 * 1. 粘贴发生在元素内部（输入框、富文本等）；
 * 2. 焦点在元素内部；
 * 3. 焦点不在任何可编辑元素上时，退回到鼠标悬停判断。
 *    焦点不一定在 body：弹窗（a-modal 等）打开时焦点停在弹窗的容器上，
 *    只认 body 的话，弹窗里的上传区域永远轮不到悬停判断。
 */
const isPasteCandidate = (el, e) => {
  if (el._dropUploadPasteDisabled) return false
  if (!document.body.contains(el)) return false

  const target = e.target
  if (target && target.nodeType === 1 && el.contains(target)) return true

  const active = document.activeElement
  if (active && active !== document.body && el.contains(active)) return true
  if (el._dropUploadHover && !isEditable(active)) return true

  return false
}

/**
 * 选出本次粘贴的接收元素
 * 1. 严格命中（粘贴目标 / 焦点 / 鼠标悬停在某个上传区域内）时取最内层的那个；
 * 2. 都没命中，但页面上只有一个可见的上传区域时也接管
 *    —— 对应「鼠标不在区域上、页面也没有焦点元素，直接按 Ctrl/Cmd + V」的常见操作。
 */
const pickPasteTarget = (e) => {
  const enabled = [...pasteEls].filter(
    (el) => !el._dropUploadPasteDisabled && document.body.contains(el) && isVisible(el)
  )
  if (!enabled.length) return null

  const strict = enabled.filter((el) => isPasteCandidate(el, e))
  if (strict.length) return strict.sort((a, b) => getDepth(b) - getDepth(a))[0]

  return enabled.length === 1 ? enabled[0] : null
}

// document 级别的粘贴处理
const handleDocumentPaste = async (e) => {
  const clipboardData = e.clipboardData || window.clipboardData
  debugLog('paste 事件', {
    已被内部组件处理: e.defaultPrevented,
    事件目标: e.target?.tagName,
    焦点元素: document.activeElement?.tagName,
    剪贴板类型: Array.from(clipboardData?.types || []),
    剪贴板条目: Array.from(clipboardData?.items || []).map((i) => `${i.kind}:${i.type}`),
    已注册上传区域: pasteEls.size
  })

  // 内部组件（如富文本编辑器）已经自行处理过就不再接管
  if (e.defaultPrevented) return

  const el = pickPasteTarget(e)
  if (!el) {
    debugLog('未匹配到上传区域，忽略本次粘贴')
    return
  }

  const files = getFilesFromClipboard(e)
  debugLog(
    '取到文件',
    files.map((f) => `${f.name} | ${f.type} | ${f.size}B`)
  )

  if (!files.length) {
    // 看起来粘了图却取不到文件：给明确提示，避免「没反应」
    if (hasImageIntent(clipboardData)) {
      e.preventDefault()
      message.warning('无法从剪贴板读取该图片，请先保存为图片文件后再拖入或粘贴')
    }
    return
  }

  const handleFiles = el._uploadMaskVNode?.component?.exposed?.handleFiles
  if (typeof handleFiles !== 'function') {
    debugLog('上传区域未就绪，exposed.handleFiles 不存在')
    return
  }

  // 有文件才拦截，纯文本粘贴不受影响
  e.preventDefault()
  e.stopPropagation()
  await handleFiles(files)
}

const handleMouseEnter = (e) => {
  e.currentTarget._dropUploadHover = true
}

const handleMouseLeave = (e) => {
  e.currentTarget._dropUploadHover = false
}

// 注册粘贴上传
const registerPaste = (el) => {
  pasteEls.add(el)
  el.addEventListener('mouseenter', handleMouseEnter)
  el.addEventListener('mouseleave', handleMouseLeave)
  if (!pasteListenerBound) {
    document.addEventListener('paste', handleDocumentPaste)
    pasteListenerBound = true
  }
}

// 注销粘贴上传
const unregisterPaste = (el) => {
  pasteEls.delete(el)
  el._dropUploadHover = false
  el.removeEventListener('mouseenter', handleMouseEnter)
  el.removeEventListener('mouseleave', handleMouseLeave)
  if (!pasteEls.size && pasteListenerBound) {
    document.removeEventListener('paste', handleDocumentPaste)
    pasteListenerBound = false
  }
}

/* ==================== 拖拽上传 ==================== */

// 创建浮窗
const createMask = (el, binding) => {
  el.style.position = 'relative'
  const container = document.createElement('div')
  const vNode = h(UploadMask, binding)
  render(vNode, container)
  const uploadEle = container.firstElementChild

  // 将浮窗元素存储到el的自定义属性中
  el._uploadMaskEle = uploadEle
  // 保存 vNode，粘贴上传需要通过组件实例调用其暴露的方法
  el._uploadMaskVNode = vNode
  el.appendChild(uploadEle)

  return uploadEle
}

// 更新浮窗
const updateMask = (el, binding) => {
  if (el._uploadMaskEle) {
    // 重新渲染组件，传递新的 props
    const container = document.createElement('div')
    const vNode = h(UploadMask, binding)
    render(vNode, container)
    const newUploadEle = container.firstElementChild

    // 替换旧的元素
    el.replaceChild(newUploadEle, el._uploadMaskEle)
    el._uploadMaskEle = newUploadEle
    el._uploadMaskVNode = vNode
    // 配置里可以关掉粘贴上传
    el._dropUploadPasteDisabled = binding?.paste === false
  }
}

// 关闭浮窗
const closeMask = (el) => {
  if (el._uploadMaskEle) {
    el._uploadMaskEle.style.display = 'none'
  }
}

// 开启浮窗
const openMask = (el) => {
  if (el._uploadMaskEle) {
    el._uploadMaskEle.style.display = 'block'
  }
}

// 拖拽进入目标区域
const handleDragEnter = (e) => {
  e.preventDefault() // 阻止默认行为
  e.stopPropagation() // 阻止事件冒泡（避免影响父元素）
  openMask(e.currentTarget)
}

// 拖拽在目标区域内移动
const handleDragOver = (e) => {
  e.preventDefault()
  e.stopPropagation()
  // 设置拖放效果
  e.dataTransfer.dropEffect = 'copy'
}

// 拖拽离开目标区域
const handleDragLeave = (e) => {
  e.preventDefault()
  e.stopPropagation()
  // 注意：需要判断是否真的离开整个区域（避免子元素触发误判）
  const relatedTarget = e.relatedTarget
  if (!e.currentTarget.contains(relatedTarget)) {
    closeMask(e.currentTarget)
  }
}

// 放下文件
const handleDrop = (e) => {
  e.preventDefault()
  e.stopPropagation()
  closeMask(e.currentTarget)
}

export default function (app) {
  app.directive('dropUpload', {
    mounted(el, binding) {
      // 创建浮窗
      createMask(el, binding.value)

      // 绑定事件监听器
      el.addEventListener('dragenter', handleDragEnter)
      el.addEventListener('dragover', handleDragOver)
      el.addEventListener('dragleave', handleDragLeave)
      el.addEventListener('drop', handleDrop)

      // 粘贴上传（配置 paste: false 可关闭）
      el._dropUploadPasteDisabled = binding.value?.paste === false
      registerPaste(el)

      // 监听 binding 值的变化
      const unwatch = watch(
        () => binding.value,
        (newVal) => {
          updateMask(el, newVal)
        },
        { deep: true } // 深度监听，确保嵌套对象的变化也能被检测到
      )
      // 将取消监听函数存储到元素上，以便在 unmounted 时清理
      el._dropUploadUnwatch = unwatch
    },
    updated(el, binding) {
      // 当指令的值更新时，也更新浮窗
      updateMask(el, binding.value)
    },
    unmounted(el) {
      // 清理监听器
      if (el._dropUploadUnwatch) {
        el._dropUploadUnwatch()
        el._dropUploadUnwatch = null
      }

      // 移除事件监听器
      el.removeEventListener('dragenter', handleDragEnter)
      el.removeEventListener('dragover', handleDragOver)
      el.removeEventListener('dragleave', handleDragLeave)
      el.removeEventListener('drop', handleDrop)

      // 移除粘贴监听
      unregisterPaste(el)

      // 清理DOM元素
      if (el._uploadMaskEle && el.contains(el._uploadMaskEle)) {
        el.removeChild(el._uploadMaskEle)
        el._uploadMaskEle = null
      }
      el._uploadMaskVNode = null
    }
  })
}
